import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Sparkles, Loader2, Check, Copy } from 'lucide-react';

const API = '/api';

const TONE_COLOR = {
  Professional: 'bg-blue-50 text-blue-700 border-blue-200',
  Casual: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Urgent: 'bg-red-50 text-red-700 border-red-200',
};

export default function VariantsPanel({ contentId, existingVariants, selectedVariant, onVariantSelected }) {
  const [variants, setVariants] = useState(existingVariants || []);
  const [generating, setGenerating] = useState(false);
  const [selecting, setSelecting] = useState('');

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/social/content/${contentId}/generate-variants`, {}, { withCredentials: true });
      setVariants(data.variants || []);
      toast.success('3 variants generated!');
    } catch {
      toast.error('Failed to generate variants');
    } finally {
      setGenerating(false);
    }
  };

  const selectVariant = async (label) => {
    setSelecting(label);
    try {
      await axios.post(`${API}/social/content/${contentId}/select-variant`, { variant_label: label }, { withCredentials: true });
      toast.success(`Variant ${label} selected as main content`);
      onVariantSelected?.(label);
    } catch {
      toast.error('Failed to select variant');
    } finally {
      setSelecting('');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-4" data-testid="variants-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-violet-500" />
          <h3 className="text-sm font-semibold text-slate-700">A/B Variants</h3>
        </div>
        <Button
          size="sm" variant="outline"
          onClick={generate}
          disabled={generating}
          data-testid="generate-variants-btn"
        >
          {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {variants.length > 0 ? 'Regenerate' : 'Generate Variants'}
        </Button>
      </div>

      {variants.length === 0 && !generating && (
        <p className="text-sm text-slate-400 py-3 text-center">Click "Generate Variants" to create A/B test versions of this content.</p>
      )}

      {generating && (
        <div className="flex items-center justify-center py-8 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span className="text-sm">AI is crafting 3 variants...</span>
        </div>
      )}

      <div className="grid gap-3">
        {variants.map((v) => {
          const isSelected = selectedVariant === v.variant_label;
          return (
            <Card
              key={v.variant_label}
              className={`transition-all ${isSelected ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : 'hover:border-slate-300'}`}
              data-testid={`variant-card-${v.variant_label}`}
            >
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs font-bold">
                      Variant {v.variant_label}
                    </Badge>
                    <Badge variant="outline" className={`text-[10px] ${TONE_COLOR[v.tone] || 'bg-slate-50'}`}>
                      {v.tone}
                    </Badge>
                    {isSelected && (
                      <Badge className="bg-indigo-500 text-white text-[10px]">Selected</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => copyToClipboard(v.caption)}
                      title="Copy caption"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant={isSelected ? 'default' : 'outline'}
                      onClick={() => selectVariant(v.variant_label)}
                      disabled={selecting === v.variant_label || isSelected}
                      data-testid={`select-variant-${v.variant_label}`}
                    >
                      {selecting === v.variant_label ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : isSelected ? (
                        <><Check className="h-3 w-3 mr-1" /> Active</>
                      ) : (
                        'Use This'
                      )}
                    </Button>
                  </div>
                </div>
                {v.hook && (
                  <p className="text-xs font-medium text-violet-600 italic">Hook: "{v.hook}"</p>
                )}
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{v.caption}</p>
                {v.hashtags && (
                  <p className="text-xs text-blue-500">{v.hashtags}</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
